import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
// import { useAccountData } from '../context/AccountContext';
import { AccountDataContext } from './AccountContext';
import { API_URL } from '../config/env';
import { createBearerJsonClient } from '../utils/apiFetchPolicy';

export const CartContext = createContext({setCart: () => {}, cart: []});

// Guest cart key for localStorage
const GUEST_CART_KEY = 'guest_cart';

// export const CartContext = createContext();
// export const useCart = () => useContext(cartContext);

export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        console.error('useAccountData must be used within an AccountDataProvider');
        // Return a default structure to prevent crashes during development
        return {
            setAuth: () => {},
            setToken: () => {},
            auth: null,
            token: null,
            email: null,
            password: null,
            addresses: [],
            setAddresses: () => {},
            paymentMethods: [],
            setPaymentMethods: () => {},
            accountDetails: null,
            setAccountDetails: () => {},
            fetchAccountData: () => {},
            isLoading: false,
        };
    }
    return context;
};


export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

// Helper function to convert backend item format to frontend format
const mapBackendItemToFrontend = (backendItem) => {
    // Backend returns: { id, itemId, quantity, name, title, short_title, price (number), image_url, category }
    // Frontend expects: { id, name/title, price (string), image, quantity, ... }
    const price = typeof backendItem.price === 'number' 
        ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(backendItem.price)
        : backendItem.price;

    return {
        id: backendItem.id || backendItem.itemId,
        name: backendItem.name || backendItem.title || '',
        title: backendItem.title || backendItem.name || '',
        shortTitle: backendItem.short_title || backendItem.shortTitle || '',
        price: price,
        image: backendItem.image_url || backendItem.image || '',
        quantity: backendItem.quantity || 1,
        category: backendItem.category || '',
        service: backendItem.service || '',
        description: backendItem.description || '',
        // Keep all original fields for compatibility
        ...backendItem
    };
};

// Helper function to convert frontend item format to backend format (for guest cart)
// const mapFrontendItemToBackend = (frontendItem) => {
//     return {
//         id: frontendItem.id,
//         itemId: frontendItem.id,
//         quantity: frontendItem.quantity || 1,
//         name: frontendItem.name || frontendItem.title || '',
//         title: frontendItem.title || frontendItem.name || '',
//         short_title: frontendItem.shortTitle || frontendItem.short_title || '',
//         price: typeof frontendItem.price === 'string' 
//             ? parseFloat(frontendItem.price.replace(/[$,]/g, '')) 
//             : frontendItem.price,
//         image_url: frontendItem.image || frontendItem.image_url || '',
//         category: frontendItem.category || '',
//     };
// };

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { auth,token } = useAccountData();
    const isInitialLoad = useRef(true);
    const isSyncing = useRef(false);

    // Create API instance with auth token
    const getApiInstance = useCallback(() => {
        return createBearerJsonClient(token, { baseURL: API_URL });
    }, [token]);

    // Load cart from backend (for logged-in users) or localStorage (for guests)
    const loadCartFromBackend = useCallback(async (mergeGuestCart = false) => {
        if (isLoading && !mergeGuestCart) return;
        setIsLoading(true);

        try {
            if (auth && auth._id && token) {
                // User is logged in - load from backend
                const api = getApiInstance();
                let backendCart = [];
                
                try {
                    const response = await api.get('/auth/cart');
                    if (response.data && response.data.success && response.data.cart) {
                        const cartData = response.data.cart;
                        if (cartData && cartData.items && Array.isArray(cartData.items)) {
                            // Map backend items to frontend format
                            backendCart = cartData.items.map(mapBackendItemToFrontend);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching cart from backend:', error);
                    // If 404 or cart doesn't exist, backend will create it on next add
                }

                // If merging guest cart, add guest items to backend
                if (mergeGuestCart) {
                    const guestCartStr = localStorage.getItem(GUEST_CART_KEY);
                    if (guestCartStr) {
                        try {
                            const guestCart = JSON.parse(guestCartStr);
                            if (Array.isArray(guestCart) && guestCart.length > 0) {
                                // Add each guest item to backend cart
                                for (const guestItem of guestCart) {
                                    try {
                                        await api.post('/auth/cart/add', {
                                            itemId: guestItem.id,
                                            quantity: guestItem.quantity || 1
                                        });
                                    } catch (addError) {
                                        console.error('Error adding guest item to backend:', addError);
                                    }
                                }
                                // Reload cart from backend after merging
                                const reloadResponse = await api.get('/auth/cart');
                                if (reloadResponse.data && reloadResponse.data.success && reloadResponse.data.cart) {
                                    const cartData = reloadResponse.data.cart;
                                    if (cartData.items && Array.isArray(cartData.items)) {
                                        backendCart = cartData.items.map(mapBackendItemToFrontend);
                                    }
                                }
                                // Clear guest cart from localStorage
                                localStorage.removeItem(GUEST_CART_KEY);
                                // Guest cart merged with backend cart
                            }
                        } catch (parseError) {
                            console.error('Error parsing guest cart for merge:', parseError);
                        }
                    }
                }

                setCart(backendCart);
                // Cart loaded from backend
            } else {
                // Guest session - load from localStorage
                const savedCart = localStorage.getItem(GUEST_CART_KEY);
                if (savedCart) {
                    try {
                        const parsedCart = JSON.parse(savedCart);
                        if (Array.isArray(parsedCart)) {
                            setCart(parsedCart);
                            // Cart loaded from localStorage
                        } else {
                            setCart([]);
                        }
                    } catch (parseError) {
                        console.error('Error parsing saved cart:', parseError);
                        localStorage.removeItem(GUEST_CART_KEY);
                        setCart([]);
                    }
                } else {
                    setCart([]);
                }
            }
        } catch (error) {
            console.error('Error loading cart:', error);
            // On error, try to load from localStorage as fallback
            if (auth && auth._id) {
                const savedCart = localStorage.getItem(GUEST_CART_KEY);
                if (savedCart) {
                    try {
                        const parsedCart = JSON.parse(savedCart);
                        if (Array.isArray(parsedCart)) {
                            setCart(parsedCart);
                        }
                    } catch (parseError) {
                        console.error('Error parsing fallback cart:', parseError);
                    }
                }
            }
        } finally {
            setIsLoading(false);
            isInitialLoad.current = false;
        }
    }, [auth, token, getApiInstance, isLoading]);

    // Track previous auth state to detect login
    const prevAuthId = useRef(auth?._id);

    // Load cart when auth state changes (login/logout) or on initial mount
    useEffect(() => {
        const currentAuthId = auth?._id;
        const wasGuest = prevAuthId.current === null || prevAuthId.current === undefined;
        const isNowLoggedIn = currentAuthId !== null && currentAuthId !== undefined;
        const isInitialMount = prevAuthId.current === undefined;
        
        // On initial mount or when auth changes
        if (isInitialMount || prevAuthId.current !== currentAuthId) {
            // If user just logged in (was guest, now logged in), merge carts
            if (!isInitialMount && wasGuest && isNowLoggedIn) {
                loadCartFromBackend(true); // Merge guest cart
            } else {
                loadCartFromBackend(false);
            }
        }
        
        prevAuthId.current = currentAuthId;
    }, [auth?._id, loadCartFromBackend]); // Only reload when user ID changes

    const addToCart = async (item) => {
        if (isSyncing.current) return;
        isSyncing.current = true;

        try {
            if (auth && auth._id && token) {
                // User is logged in - add to backend
                const api = getApiInstance();
                const response = await api.post('/auth/cart/add', {
                    itemId: item.id,
                    quantity: 1
                });

                if (response.data && response.data.success && response.data.cart) {
                    // Update local cart from backend response
                    const cartData = response.data.cart;
                    if (cartData.items && Array.isArray(cartData.items)) {
                        const mappedItems = cartData.items.map(mapBackendItemToFrontend);
                        setCart(mappedItems);
                        // Item added to cart via backend
                    }
                }
            } else {
                // Guest session - update localStorage
                setCart(prevCart => {
                    const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
                    let newCart;
                    if (existingItem) {
                        newCart = prevCart.map(cartItem =>
                            cartItem.id === item.id
                                ? { ...cartItem, quantity: cartItem.quantity + 1 }
                                : cartItem
                        );
                    } else {
                        newCart = [...prevCart, { ...item, quantity: 1 }];
                    }
                    // Save to localStorage
                    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newCart));
                    return newCart;
                });
            }
        } catch (error) {
            console.error('Error adding item to cart:', error);
            // Fallback to local update on error
            setCart(prevCart => {
                const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
                if (existingItem) {
                    return prevCart.map(cartItem =>
                        cartItem.id === item.id
                            ? { ...cartItem, quantity: cartItem.quantity + 1 }
                            : cartItem
                    );
                }
                return [...prevCart, { ...item, quantity: 1 }];
            });
        } finally {
            isSyncing.current = false;
        }
    };

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1) return;
        if (isSyncing.current) return;
        isSyncing.current = true;

        try {
            if (auth && auth._id && token) {
                // User is logged in - update backend
                const api = getApiInstance();
                const response = await api.put('/auth/cart/update', {
                    itemId: itemId,
                    quantity: newQuantity
                });

                if (response.data && response.data.success && response.data.cart) {
                    // Update local cart from backend response
                    const cartData = response.data.cart;
                    if (cartData.items && Array.isArray(cartData.items)) {
                        const mappedItems = cartData.items.map(mapBackendItemToFrontend);
                        setCart(mappedItems);
                        // Cart quantity updated via backend
                    }
                }
            } else {
                // Guest session - update localStorage
                setCart(prevCart => {
                    const newCart = prevCart.map(item =>
                        item.id === itemId
                            ? { ...item, quantity: newQuantity }
                            : item
                    );
                    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newCart));
                    return newCart;
                });
            }
        } catch (error) {
            console.error('Error updating cart quantity:', error);
            // Fallback to local update on error
            setCart(prevCart =>
                prevCart.map(item =>
                    item.id === itemId
                        ? { ...item, quantity: newQuantity }
                        : item
                )
            );
        } finally {
            isSyncing.current = false;
        }
    };

    const removeFromCart = async (itemId) => {
        if (isSyncing.current) return;
        isSyncing.current = true;

        try {
            if (auth && auth._id && token) {
                // User is logged in - remove from backend
                const api = getApiInstance();
                const response = await api.delete('/auth/cart/remove', {
                    data: { itemId: itemId }
                });

                if (response.data && response.data.success && response.data.cart) {
                    // Update local cart from backend response
                    const cartData = response.data.cart;
                    if (cartData.items && Array.isArray(cartData.items)) {
                        const mappedItems = cartData.items.map(mapBackendItemToFrontend);
                        setCart(mappedItems);
                        // Item removed from cart via backend
                    }
                }
            } else {
                // Guest session - update localStorage
                setCart(prevCart => {
                    const newCart = prevCart.filter(item => item.id !== itemId);
                    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newCart));
                    return newCart;
                });
            }
        } catch (error) {
            console.error('Error removing item from cart:', error);
            // Fallback to local update on error
            setCart(prevCart => prevCart.filter(item => item.id !== itemId));
        } finally {
            isSyncing.current = false;
        }
    };

    const saveCart = async (cart, userId, authToken) => {
        if (userId && authToken) {
            // User is logged in - add to backend
            const api = getApiInstance();

            cart.map(item=>{    
                const response = api.post('/auth/cart/add', {
                    itemId: item.id,
                    quantity: item.quantity
                });
                if (response.data && response.data.success && response.data.cart) {
                    // Item added to cart via backend
                }
                return item;
            });
        }
    };

    const clearCart = async () => {
        if (isSyncing.current) return;
        isSyncing.current = true;

        try {
            if (auth && auth._id && token) {
                // User is logged in - clear backend cart
                const api = getApiInstance();
                const response = await api.delete('/auth/cart/clear');

                if (response.data && response.data.success && response.data.cart) {
                    // Update local cart from backend response
                    const cartData = response.data.cart;
                    if (cartData.items && Array.isArray(cartData.items)) {
                        const mappedItems = cartData.items.map(mapBackendItemToFrontend);
                        setCart(mappedItems);
                        // Cart cleared via backend
                    } else {
                        setCart([]);
                    }
                }
            } else {
                // Guest session - clear localStorage
                setCart([]);
                localStorage.removeItem(GUEST_CART_KEY);
            }
        } catch (error) {
            console.error('Error clearing cart:', error);
            // Fallback to local clear on error
            setCart([]);
            if (!auth || !auth._id) {
                localStorage.removeItem(GUEST_CART_KEY);
            }
        } finally {
            isSyncing.current = false;
        }
    };

    return (
        <CartContext.Provider value={{
            cart,
            setCart,
            saveCart,
            addToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            isLoading,
            loadCart: loadCartFromBackend
        }}>
            {children}
        </CartContext.Provider>
    );
}; 